import type { Component, ComponentProps } from "solid-js";

/**
 * Inline SVG illustrations with gym identity. Theme-aware: they use
 * `currentColor` + repo tokens (accent/muted) through classes, with no hardcoded
 * colors. Decorative by default (`aria-hidden`); the empty-state text provides
 * accessible context.
 */
type IllustrationProps = ComponentProps<"svg">;

/** Energetic dumbbell for "no routines" / empty catalogs. */
export const DumbbellIllustration: Component<IllustrationProps> = (props) => (
  <svg
    viewBox="0 0 200 200"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    {/* Halo */}
    <circle cx="100" cy="100" r="78" class="fill-primary/5" />
    <circle cx="100" cy="100" r="78" class="stroke-primary/15" stroke-width="1.5" />
    {/* Barra */}
    <rect
      x="58"
      y="92"
      width="84"
      height="16"
      rx="8"
      class="fill-primary/15 stroke-primary"
      stroke-width="3"
    />
    {/* Discos izquierda */}
    <rect x="34" y="70" width="18" height="60" rx="7" class="fill-primary stroke-primary" />
    <rect x="20" y="80" width="14" height="40" rx="6" class="fill-primary/60 stroke-primary" />
    {/* Discos derecha */}
    <rect x="148" y="70" width="18" height="60" rx="7" class="fill-primary stroke-primary" />
    <rect x="166" y="80" width="14" height="40" rx="6" class="fill-primary/60 stroke-primary" />
    {/* Brillo */}
    <path
      d="M70 86c12-4 48-4 60 0"
      class="stroke-primary-foreground/40"
      stroke-width="2.5"
      stroke-linecap="round"
    />
  </svg>
);

/** Progress flame/rocket for "no active session" / dashboards. */
/**
 * Dropped disk/weight with an alert spark for error states. Theme-aware:
 * uses primary/destructive tokens through classes, with no hardcoded colors.
 */
export const ErrorIllustration: Component<IllustrationProps> = (props) => (
  <svg
    viewBox="0 0 200 200"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <circle cx="100" cy="100" r="78" class="fill-destructive/5" />
    <circle cx="100" cy="100" r="78" class="stroke-destructive/15" stroke-width="1.5" />
    {/* Disco volcado */}
    <ellipse cx="100" cy="138" rx="46" ry="12" class="fill-destructive/10" />
    <circle
      cx="100"
      cy="112"
      r="34"
      class="fill-destructive/10 stroke-destructive"
      stroke-width="3.5"
    />
    <circle
      cx="100"
      cy="112"
      r="11"
      class="fill-destructive/20 stroke-destructive"
      stroke-width="3"
    />
    {/* Signo de alerta */}
    <path d="M100 96v18" class="stroke-destructive" stroke-width="4" stroke-linecap="round" />
    <circle cx="100" cy="124" r="2.6" class="fill-destructive" />
    {/* Chispa */}
    <path
      d="M150 58l-10 14h8l-6 16 16-20h-8l4-10z"
      class="fill-primary stroke-primary"
      stroke-width="2"
      stroke-linejoin="round"
    />
  </svg>
);

/** Stopwatch/empty log for "no history". */
export const HistoryIllustration: Component<IllustrationProps> = (props) => (
  <svg
    viewBox="0 0 200 200"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <circle cx="100" cy="100" r="78" class="fill-primary/5" />
    <circle cx="100" cy="100" r="78" class="stroke-primary/15" stroke-width="1.5" />
    {/* Reloj */}
    <circle cx="100" cy="104" r="42" class="fill-primary/10 stroke-primary" stroke-width="3.5" />
    {/* Botón superior */}
    <rect x="90" y="52" width="20" height="10" rx="3" class="fill-primary" />
    {/* Manecillas */}
    <path
      d="M100 104V80M100 104l18 10"
      class="stroke-primary"
      stroke-width="3.5"
      stroke-linecap="round"
    />
    <circle cx="100" cy="104" r="4" class="fill-primary" />
  </svg>
);
